










































import {
  ref,
  computed,
  watch,
  onMounted,
  defineComponent,
  useRoute,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import cartGetters from '~/modules/checkout/getters/cartGetters';
import { useCart, useUiNotification } from '~/composables';

import CartPreview from '~/modules/checkout/components/CartPreview.vue';
import CheckoutBreadcrumbs from '~/modules/checkout/components/CheckoutBreadcrumbs.vue';
import UserAccount from '~/modules/checkout/pages/Checkout/UserAccount.vue';
import Shipping from '~/modules/checkout/pages/Checkout/Shipping.vue';
import Payment from '~/modules/checkout/pages/Checkout/Payment.vue';
import { SfHeading } from '~/components';
import { sendEcommerce } from '~/utilities/helpers';

export default defineComponent({
  name: 'CheckoutPage',
  components: {
    SfHeading,
    UserAccount,
    Shipping,
    Payment,
    CartPreview,
    CheckoutBreadcrumbs
  },
  head() {
    return {
      script: [
        {
          src: 'https://js.stripe.com/v3'
        }
      ],
    };
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { app } = useContext();
    const { path } = route.value;
    const { cart, load: loadCart } = useCart();
    const products = computed(() => cartGetters.getItems(cart.value));
    const currentStep = computed(() => path.split('/').pop());
    const isThankYou = computed(() => currentStep.value === 'thank-you');
    const firstName = ref('');
    const lastName = ref('');
    const isShowShipping = ref(false);
    const isShippingAndBillingSubmit = ref(false);
    const isShowPayment = computed(() => isShippingAndBillingSubmit.value && isShowShipping.value);
    const paymentComponent = ref(null);
    const { send: sendNotification } = useUiNotification();

    const handleUserFormSubmit = (status, formData) => {
      if (formData) {
        firstName.value = formData.firstname;
        lastName.value = formData.lastname;
      }
      isShowShipping.value = status;
      isShippingAndBillingSubmit.value = false;
    };
    const handleShippingAndBillingSubmit = (status) => {
      isShippingAndBillingSubmit.value = status;
    };

    watch(
      paymentComponent,
      (newValue: any) => {
        if (newValue) {
          const headerHeight = (document.querySelector('.sf-header__top-wrapper') as HTMLElement).offsetHeight;
          const scrollPosition = newValue.$el.offsetTop - headerHeight;
          window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
      });

    // Ecommerce and Pixels
    const productsData = cart.value?.items?.map(
      item => Object.assign(
        item.product,
        { quantity: item.quantity },
        { configurable_product_options_selection: { variant: { uid: item.uid } } }
      )
    ) || [];

    const isOrderProcessing = ref(false);

    const handelProcessOrderStatus = (status: boolean) => {
      isOrderProcessing.value = status;
    };

    const sendEcommerceBeginCheckout = () => {
      const ecommerceAdditional = {
        'value': cart.value?.prices.grand_total.value,
        'currency': app.$vsf.$magento.config.state.getCurrency().toUpperCase(),
      };
      const additional = {
        'goods_id': cart.value?.items?.map(item => item.product.sku),
        'goods_price': cart.value?.prices?.grand_total.value,
      };

      sendEcommerce('begin_checkout', 'cart', productsData, additional, ecommerceAdditional);
    };

    const sendEcommercePurchase = (orderId) => {
      const additional = {
        'goods_id': cart.value?.items.map(item => item.product.sku),
        'goods_price': cart.value?.prices.grand_total.value,
      };
      const ecommerceAdditional = {
        'transaction_id': orderId,
        'value': cart.value?.prices.grand_total.value,
        'currency': app.$vsf.$magento.config.state.getCurrency().toUpperCase(),
        'shipping': cart.value?.shipping_addresses[0].selected_shipping_method.amount.value,
        'coupon': cart.value?.applied_coupons?.map(item => item.code['code']) || '',
      };
      sendEcommerce('purchase', 'cart', productsData, additional, ecommerceAdditional);
    };

    onMounted(async () => {
      await loadCart();
      if (products.value.length === 0 && currentStep.value !== 'thank-you') {
        await router.push(app.localePath('/'));
      }
      if (route.value.query?.redirect_status && route.value.query.redirect_status === 'failed') {
        sendNotification({
          id: Symbol('payment_intent_error'),
          message: app.i18n.tc('Payment failed. Please try another payment method.'),
          type: 'danger',
          icon: 'info_shield',
        });
      }

      sendEcommerceBeginCheckout();
    });

    return {
      paymentComponent,
      isThankYou,
      currentStep,
      firstName,
      lastName,
      isShowShipping,
      isShowPayment,
      handleUserFormSubmit,
      handleShippingAndBillingSubmit,
      sendEcommercePurchase,
      isOrderProcessing,
      handelProcessOrderStatus
    };
  },
});
